"use client";

import { useEffect, useRef, useState } from "react";

import {
  AlertDialog,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import { toastManager } from "~/components/ui/toast";
import { useProjectScriptRunStore, type ProjectScriptRun } from "~/projectScriptRunStore";
import { CommandOutputBlock } from "./CommandOutputBlock";

type ToastId = ReturnType<typeof toastManager.add>;
type RunToastType = "info" | "loading" | "success" | "error" | "warning";

/**
 * Bridges background project-action runs to the global toast manager and hosts
 * the read-only output modal. Mount once at the app root.
 *
 * A background action produces no terminal to look at, so this toast is the
 * only feedback the user gets: it stays in `loading` while the command runs and
 * flips to success/error with a "Ver output" action when it ends.
 */
export function ProjectScriptRunToastBridge() {
  const runs = useProjectScriptRunStore((state) => state.runs);
  const toastIdsRef = useRef(new Map<string, ToastId>());
  const lastPhaseRef = useRef(new Map<string, ProjectScriptRun["phase"]>());
  const [modalRunId, setModalRunId] = useState<string | null>(null);

  useEffect(() => {
    for (const run of Object.values(runs)) {
      const lastPhase = lastPhaseRef.current.get(run.runId);
      if (lastPhase === run.phase) continue;
      lastPhaseRef.current.set(run.runId, run.phase);

      const descriptor = buildToastDescriptor(run, () => setModalRunId(run.runId));
      const existing = toastIdsRef.current.get(run.runId);

      if (existing) {
        toastManager.update(existing, descriptor);
      } else {
        const toastId = toastManager.add({
          type: descriptor.type,
          title: descriptor.title,
          description: descriptor.description,
          ...(descriptor.actionProps ? { actionProps: descriptor.actionProps } : {}),
          data: { allowCrossThreadVisibility: true },
        });
        toastIdsRef.current.set(run.runId, toastId);
      }
    }
  }, [runs]);

  const activeRun = modalRunId ? (runs[modalRunId] ?? null) : null;

  return (
    <ProjectScriptRunOutputDialog
      open={activeRun !== null}
      run={activeRun}
      onClose={() => setModalRunId(null)}
    />
  );
}

function buildToastDescriptor(
  run: ProjectScriptRun,
  openModal: () => void,
): {
  type: RunToastType;
  title: string;
  description: string;
  actionProps?: { children: string; onClick: () => void };
} {
  if (run.phase === "started") {
    return {
      type: "loading",
      title: `Ejecutando "${run.scriptName}"`,
      description: truncateCommand(run.command),
    };
  }

  if (run.phase === "completed") {
    return {
      type: "success",
      title: `"${run.scriptName}" completado`,
      description: `${run.result?.durationMs ?? 0}ms · ${truncateCommand(run.command)}`,
      actionProps: { children: "Ver output", onClick: openModal },
    };
  }

  return {
    type: "error",
    title: `"${run.scriptName}" falló`,
    description: failureDescription(run),
    actionProps: { children: "Ver output", onClick: openModal },
  };
}

function failureDescription(run: ProjectScriptRun): string {
  if (run.result?.timedOut) return "La ejecución superó el tiempo límite";
  if (run.result) return `Salió con código ${run.result.exitCode ?? "null"}`;
  return run.message ?? "No se pudo ejecutar el comando";
}

function ProjectScriptRunOutputDialog({
  open,
  run,
  onClose,
}: {
  readonly open: boolean;
  readonly run: ProjectScriptRun | null;
  readonly onClose: () => void;
}) {
  return (
    <AlertDialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <AlertDialogPopup>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {run ? `${run.scriptName} — output` : "Acción — output"}
          </AlertDialogTitle>
          <AlertDialogDescription>{run ? run.cwd : ""}</AlertDialogDescription>
        </AlertDialogHeader>
        <div className="mx-6 flex max-h-[60vh] flex-col gap-3 overflow-auto">
          {run?.result ? (
            <CommandOutputBlock
              command={run.command}
              statusLabel={
                run.result.timedOut
                  ? "timeout"
                  : run.result.exitCode === 0
                    ? "ok"
                    : `exit ${run.result.exitCode ?? "null"}`
              }
              durationMs={run.result.durationMs}
              stdout={run.result.stdout}
              stderr={run.result.stderr}
              stdoutTruncated={run.result.stdoutTruncated}
              stderrTruncated={run.result.stderrTruncated}
            />
          ) : (
            <p className="text-sm text-muted-foreground">{run?.message ?? "Sin output todavía."}</p>
          )}
        </div>
        <div className="flex flex-row-reverse gap-2 p-6 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cerrar
          </Button>
        </div>
      </AlertDialogPopup>
    </AlertDialog>
  );
}

function truncateCommand(command: string): string {
  return command.length > 60 ? `${command.slice(0, 57)}…` : command;
}
